import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const FLAG_KEY = "offernest_onboarded";

const prefersReducedMotion = () => {
    try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; }
    catch { return false; }
};

const finish = (nav) => {
    try { localStorage.setItem(FLAG_KEY, "true"); } catch { /* ignore */ }
    nav("/home", { replace: true });
};

function Dots({ active, total = 3 }) {
    return (
        <div className="flex items-center justify-center gap-2" data-testid="onboarding-dots">
            {Array.from({ length: total }).map((_, i) => (
                <span
                    key={i}
                    className={`h-2 rounded-full transition-all ${i === active ? "w-6 bg-[var(--brand-orange)]" : "w-2 bg-neutral-300"}`}
                />
            ))}
        </div>
    );
}

function Slide({ children, kind, reduced }) {
    if (reduced) return <div className="flex h-full flex-col">{children}</div>;
    const variants = kind === "fade"
        ? { initial: { opacity: 0, scale: 0.98 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 1.02 } }
        : { initial: { x: 40, opacity: 0 }, animate: { x: 0, opacity: 1 }, exit: { x: -40, opacity: 0 } };
    return (
        <motion.div
            {...variants}
            transition={{ duration: kind === "fade" ? 0.4 : 0.35, ease: "easeOut" }}
            className="flex h-full flex-col"
        >
            {children}
        </motion.div>
    );
}

export default function OnboardingWelcome() {
    const nav = useNavigate();
    const [step, setStep] = useState(0); // 0 splash, 1 discover, 2 savings
    const reduced = prefersReducedMotion();

    // Splash auto-advance
    useEffect(() => {
        if (step !== 0) return undefined;
        const t = setTimeout(() => setStep(1), 1800);
        return () => clearTimeout(t);
    }, [step]);

    const skip = () => finish(nav);
    const next = () => (step >= 2 ? finish(nav) : setStep((s) => s + 1));

    return (
        <div className="min-h-screen w-full" style={{ background: "#F8FAFC" }} data-testid="onboarding-page">
            <div className="mx-auto flex min-h-screen w-full max-w-md flex-col overflow-hidden bg-white md:my-8 md:min-h-[720px] md:rounded-3xl md:shadow-xl">
                <AnimatePresence mode="wait">
                    {step === 0 && (
                        <Slide key="splash" kind="fade" reduced={reduced}>
                            <button
                                type="button"
                                onClick={() => setStep(1)}
                                data-testid="onboarding-splash"
                                className="flex h-full flex-1 flex-col items-center justify-between px-8 pt-16 pb-10 text-center"
                            >
                                <div className="mt-4 flex flex-col items-center gap-2">
                                    <img
                                        src="/logo.png"
                                        alt="OfferNest"
                                        className="h-32 md:h-40 w-auto max-w-full object-contain"
                                        data-testid="onboarding-splash-logo"
                                    />
                                    <p className="mt-2 font-heading text-base font-medium text-[var(--text-muted)]">
                                        Best Offers. Every Day!
                                    </p>
                                </div>
                                <img
                                    src="/onboarding/splash.png"
                                    alt=""
                                    className="mt-8 h-56 w-56 max-w-full object-contain sm:h-64 sm:w-64"
                                    data-testid="onboarding-splash-img"
                                />
                                <span className="font-body text-[11px] uppercase tracking-widest text-neutral-300">tap or wait</span>
                            </button>
                        </Slide>
                    )}
                    {step === 1 && (
                        <Slide key="discover" kind="slide" reduced={reduced}>
                            <SlideContent
                                testid="onboarding-discover"
                                img="/onboarding/discover.png"
                                heading="Discover Best Offers Near You"
                                desc="Find amazing deals, discounts and exclusive offers from shops in your city."
                                activeDot={1}
                                onSkip={skip}
                                onNext={next}
                                ctaLabel="Next"
                                ctaTestid="onboarding-next"
                            />
                        </Slide>
                    )}
                    {step === 2 && (
                        <Slide key="savings" kind="slide" reduced={reduced}>
                            <SlideContent
                                testid="onboarding-savings"
                                img="/onboarding/savings.png"
                                heading="Shop More, Save More"
                                desc="Exciting offers on fashion, electronics, food & more."
                                activeDot={2}
                                onSkip={skip}
                                onNext={next}
                                ctaLabel="Get Started"
                                ctaTestid="onboarding-get-started"
                            />
                        </Slide>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

function SlideContent({ testid, img, heading, desc, activeDot, onSkip, onNext, ctaLabel, ctaTestid }) {
    return (
        <div data-testid={testid} className="flex h-full flex-1 flex-col px-8 pt-12 pb-8 md:pt-16">
            <div className="flex flex-1 flex-col items-center text-center">
                <img src={img} alt="" className="h-56 w-56 max-w-full object-contain sm:h-64 sm:w-64" data-testid={`${testid}-img`} />
                <h1 className="mt-8 font-heading text-2xl font-bold text-[var(--text-primary)] sm:text-[28px]">
                    {heading}
                </h1>
                <p className="mt-3 max-w-sm font-body text-sm text-[var(--text-muted)]">
                    {desc}
                </p>
            </div>
            <div className="mt-6 flex flex-col items-center gap-6">
                <Dots active={activeDot} />
                <div className="flex w-full items-center justify-between">
                    <button
                        type="button"
                        onClick={onSkip}
                        data-testid="onboarding-skip"
                        className="font-body text-sm font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                    >
                        Skip
                    </button>
                    <button
                        type="button"
                        onClick={onNext}
                        data-testid={ctaTestid}
                        className="btn-primary"
                    >
                        {ctaLabel} →
                    </button>
                </div>
            </div>
        </div>
    );
}
